import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { OrderItem } from "../../models/order";
import { useLoaderData, useNavigate, useLocation } from "react-router";
import { storesAPI } from "@api/main";
import { usePageTitle } from '../../hooks/usePageTitle';
import { pageTrackingService } from '../../services/pageTracking';
import { analytics } from '../../services/analytics';
import { useToast } from '../../contexts/ToastContext';
import SupportEmailLink from '../../components/SupportEmailLink/SupportEmailLink';
import DiscordInviteButton from '../../components/DiscordInviteButton/DiscordInviteButton';
import { gtagService } from '../../services/gtag';
import { optimizeImageUrl, generateProductDetailsUrl } from '@utils/urlUtils';
import Header from '../HomeRedesign/components/Header/Header';
import Footer from '../HomeRedesign/components/Footer/Footer';
import Loader from '@components/Loader/Loader';
import styles from './OrdersRedesign.module.scss';

const RedeemKey: React.FC<{
  redemption_id: OrderItem["redemption_ids"][number];
}> = ({ redemption_id }) => {
  const [key, setKey] = useState<string | { [key: string]: string } | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchKey = async () => {
    setIsLoading(true);
    try {
      const key = await storesAPI.getRedeemKey(redemption_id.id);
      // Post-purchase activation signal: the buyer revealed their key.
      analytics.track('redeem_key_viewed', { redemption_id: redemption_id.id });
      
      let parsedKey: string | { [key: string]: string } | null = key;
      
      if (typeof key === 'string') {
        const trimmed = key.trim();
        if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
          try {
            const parsed = JSON.parse(key);
            if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
              parsedKey = parsed;
            }
          } catch (e) {
            // Not a valid JSON string, keep as is
          }
        }
      }
      
      setKey(parsedKey);
    } catch (error) {
      console.error('Error fetching key:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const isKeyObject = key !== null && 
                      typeof key === 'object' && 
                      !Array.isArray(key) &&
                      Object.prototype.toString.call(key) === '[object Object]';

  return (
    <div className={styles.redeemKeyContainer}>
      {!key && (
        <button
          className={`${styles.claimCodeButton} ${isLoading ? styles.loading : ''}`}
          onClick={fetchKey}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader size={16} color="#FFFFFF" />
              <span>Claiming...</span>
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 1L3 3.5V7C3 10.5 5.5 13.5 8 14.5C10.5 13.5 13 10.5 13 7V3.5L8 1Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6 8L7.5 9.5L10 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Claim Code</span>
            </>
          )}
        </button>
      )}
      
      {key && typeof key === 'string' && (
        <div className={styles.keyDisplay}>
          <div 
            className={`${styles.keyValue} ${styles.copyable}`}
            onClick={() => {
              navigator.clipboard.writeText(key);
              setIsCopied(true);
              setTimeout(() => setIsCopied(false), 2000);
            }}
            title="Click to copy"
          >
            <code>{key}</code>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.5 4.5V2.5C5.5 1.94772 5.94772 1.5 6.5 1.5H13.5C14.0523 1.5 14.5 1.94772 14.5 2.5V9.5C14.5 10.0523 14.0523 10.5 13.5 10.5H11.5M2.5 5.5H9.5C10.0523 5.5 10.5 5.94772 10.5 6.5V13.5C10.5 14.0523 10.0523 14.5 9.5 14.5H2.5C1.94772 14.5 1.5 14.0523 1.5 13.5V6.5C1.5 5.94772 1.94772 5.5 2.5 5.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {isCopied && (
              <span className={styles.copyFeedback}>Copied!</span>
            )}
          </div>
        </div>
      )}
      
      {isKeyObject && (
        <div className={styles.keyObjectContainer}>
          <div className={styles.keyObjectGrid}>
            {Object.entries(key).map(([k, v]) => {
              const valueToCopy = v || '-';
              const handleCopyValue = (value: string, field: string) => {
                if (value && value !== '-') {
                  navigator.clipboard.writeText(value);
                  setCopiedField(field);
                  setTimeout(() => setCopiedField(null), 2000);
                }
              };
              
              return (
                <div key={k} className={styles.keyObjectItem}>
                  <div className={styles.keyObjectLabel}>{k}</div>
                  <div 
                    className={`${styles.keyObjectValue} ${valueToCopy !== '-' ? styles.copyable : ''} ${copiedField === k ? styles.copied : ''}`}
                    onClick={() => handleCopyValue(valueToCopy, k)}
                    title={valueToCopy !== '-' ? "Click to copy" : ""}
                  >
                    <code>{valueToCopy}</code>
                    {valueToCopy !== '-' && (
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5.5 4.5V2.5C5.5 1.94772 5.94772 1.5 6.5 1.5H13.5C14.0523 1.5 14.5 1.94772 14.5 2.5V9.5C14.5 10.0523 14.0523 10.5 13.5 10.5H11.5M2.5 5.5H9.5C10.0523 5.5 10.5 5.94772 10.5 6.5V13.5C10.5 14.0523 10.0523 14.5 9.5 14.5H2.5C1.94772 14.5 1.5 14.0523 1.5 13.5V6.5C1.5 5.94772 1.94772 5.5 2.5 5.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                    {copiedField === k && (
                      <span className={styles.copyFeedback}>Copied!</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const INFO_TITLES = {
  failed: "Your refund will be processed within 48 hours.",
  processing: "It may take upto 24 hours for the key to be delivered.",
};

const INFO_DESC = {
  failed: "You can also contact support if you do not receive the refund.",
  processing: (
    <span style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
      <span>
        You will be notified about the same, incase you face any issue please reach out to{" "}
        <SupportEmailLink />{" "}
        or join our Discord channel:
      </span>
      <span style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <DiscordInviteButton />
      </span>
    </span>
  ),
};

const OrderStatus: React.FC<{
  status: OrderItem["order_item_status"];
  orderId: string;
  itemId: string;
  onTooltipChange?: (isActive: boolean) => void;
}> = ({ status, orderId, itemId, onTooltipChange }) => {
  const [infoMessage, setInfoMessage] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const clickedRef = React.useRef(false);
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const showInfoIcon = ["failed", "processing"].includes(status);
  const infoTitle = INFO_TITLES[status as keyof typeof INFO_TITLES];
  const infoDesc = INFO_DESC[status as keyof typeof INFO_DESC];

  React.useLayoutEffect(() => {
    if (!infoMessage || !buttonRef.current || !tooltipRef.current) return;
    const tooltipEl = tooltipRef.current;

    const updatePosition = () => {
      if (!buttonRef.current || !tooltipRef.current) return;
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const tooltip = tooltipRef.current;
      const vw = window.innerWidth;
      const edge = 16;
      const z = '8000';

      if (isMobile) {
        tooltip.style.position = 'fixed';
        tooltip.style.top = `${buttonRect.bottom + 12}px`;
        tooltip.style.left = '50%';
        tooltip.style.right = 'auto';
        tooltip.style.bottom = 'auto';
        tooltip.style.transform = 'translateX(-50%)';
        tooltip.style.maxWidth = `${Math.min(320, vw - 40)}px`;
        tooltip.style.width = 'max-content';
        tooltip.style.minWidth = '';
        tooltip.style.zIndex = z;
        tooltip.style.marginLeft = '0';
        tooltip.style.marginRight = '0';
      } else {
        // Portaled fixed: align panel right edge to icon, grow left — avoids grid/card clipping on desktop
        const rightInset = vw - buttonRect.right;
        const avail = buttonRect.right - edge;
        const maxW = Math.min(400, Math.max(200, avail));
        tooltip.style.position = 'fixed';
        tooltip.style.right = `${rightInset}px`;
        tooltip.style.left = 'auto';
        tooltip.style.bottom = 'auto';
        tooltip.style.top = `${buttonRect.top - 12}px`;
        tooltip.style.transform = 'translateY(-100%)';
        tooltip.style.maxWidth = `${maxW}px`;
        tooltip.style.width = 'max-content';
        tooltip.style.minWidth = `${Math.min(280, maxW)}px`;
        tooltip.style.zIndex = z;
        tooltip.style.marginLeft = '0';
        tooltip.style.marginRight = '0';
      }
    };

    updatePosition();
    const raf = window.requestAnimationFrame(updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      tooltipEl.style.cssText = '';
    };
  }, [infoMessage, isMobile]);

  const showInfoMsg = (type: any) => {
    if(type === 'click'){
      clickedRef.current = true;
      setInfoMessage(true);
      onTooltipChange?.(true);
      setTimeout(() => {
        setInfoMessage(false);
        clickedRef.current = false;
        onTooltipChange?.(false);
      }, 8000);
    }
    if(type === 'hover'){
      if (!clickedRef.current) {
        setInfoMessage(true);
        onTooltipChange?.(true);
      }
    }
    if(type === 'leave' && !clickedRef.current){
      setInfoMessage(false);
      onTooltipChange?.(false);
    }
  };

  const handleDownloadInvoice = async () => {
    try {
      const blob = await storesAPI.downloadInvoice(orderId, itemId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${orderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Failed to download invoice');
    }
  };

  const getStatusDisplay = () => {
    if (status === 'processing') return 'Pending';
    if (status && typeof status === 'string' && status.length > 0) {
      return status.charAt(0).toUpperCase() + status.slice(1);
    }
    return status;
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'completed':
        return (
          <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M5 8L7 10L11 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'processing':
        return (
          <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 2"/>
            <path d="M8 4V8L10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        );
      case 'failed':
        return (
          <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M6 6L10 10M10 6L6 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.orderStatusContainer}>
      <div className={`${styles.statusBadge} ${styles[`status-${status}`]} ${infoMessage ? styles.tooltipActive : ""}`}>
        {getStatusIcon()}
        <span className={styles.statusText}>{getStatusDisplay()}</span>
        {showInfoIcon && (
          <button
            ref={buttonRef}
            className={styles.infoIconButton}
            onClick={() => showInfoMsg('click')}
            onMouseEnter={() => showInfoMsg('hover')}
            onMouseLeave={() => showInfoMsg('leave')}
            aria-label="Order information"
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/icons/${infoMessage ? "info-active" : "info-inactive"}.svg`}
              alt="info-icon"
            />
            {infoMessage &&
              typeof document !== 'undefined' &&
              createPortal(
                <div
                  ref={tooltipRef}
                  className={`${styles.infoTooltip} ${styles.infoTooltipPortaled}`}
                  role="tooltip"
                >
                  <div className={styles.infoTooltipTitle}>{infoTitle}</div>
                  <div className={styles.infoTooltipDesc}>{infoDesc}</div>
                </div>,
                document.body
              )}
          </button>
        )}
      </div>
      
      {status === "completed" && (
        <div className={styles.statusActions}>
          <button
            className={styles.actionButton}
            onClick={handleDownloadInvoice}
            title="Download Invoice"
            aria-label="Download Invoice"
          >
            <svg width="18" height="18" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 11V1M8 11L5 8M8 11L11 8M2 11H14V14C14 14.5523 13.5523 15 13 15H3C2.44772 15 2 14.5523 2 14V11Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className={styles.actionButtonText}>Invoice</span>
          </button>
        </div>
      )}
    </div>
  );
};

const OrderCard: React.FC<{
  order: OrderItem;
}> = ({ order }) => {
  const navigate = useNavigate();
  const [isTooltipActive, setIsTooltipActive] = useState(false);

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth()+1).toString().padStart(2, '0')}.${date.getFullYear()}`;
  };

  const productUrl = order.product_id
    ? generateProductDetailsUrl({ id: order.product_id, title: order.title })
    : null;

  const goToProduct = () => {
    if (productUrl) navigate(productUrl);
  };

  const onProductKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      goToProduct();
    }
  };

  const orderImage = (
    <div className={styles.orderImageWrapper}>
      <img
        src={
          optimizeImageUrl(order.poster) ||
          `${process.env.PUBLIC_URL || ''}/images/product-placeholder.svg`
        }
        loading="lazy"
        onError={(e) => {
          const target = e.currentTarget as HTMLImageElement;
          target.onerror = null;
          target.src = `${process.env.PUBLIC_URL || ''}/images/product-placeholder.svg`;
        }}
        alt={order.title}
        className={styles.orderImage}
      />
    </div>
  );

  return (
    <div className={`${styles.orderCard} ${isTooltipActive ? styles.tooltipActive : ""}`}>
      <div className={styles.orderCardHeader}>
        <div className={styles.orderProductInfo}>
          {productUrl ? (
            <div
              className={styles.orderProductImageLink}
              role="link"
              tabIndex={0}
              onClick={goToProduct}
              onKeyDown={onProductKeyDown}
              aria-label={`View product: ${order.title}`}
            >
              {orderImage}
            </div>
          ) : (
            orderImage
          )}
          <div className={styles.orderDetails}>
            {productUrl ? (
              <h3
                className={`${styles.orderTitle} ${styles.orderTitleInteractive}`}
                role="link"
                tabIndex={0}
                onClick={goToProduct}
                onKeyDown={onProductKeyDown}
              >
                {order.title}
              </h3>
            ) : (
              <h3 className={styles.orderTitle}>{order.title}</h3>
            )}
            <div className={styles.orderMeta}>
              {order.store && (
                <>
                  <span className={styles.orderStore}>{order.store}</span>
                  <span className={styles.orderSeparator}>•</span>
                </>
              )}
              <span className={styles.orderId}>Order #{order.order_id}</span>
            </div>
            <div className={styles.orderDate}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2H4C2.89543 2 2 2.89543 2 4V12C2 13.1046 2.89543 14 4 14H12C13.1046 14 14 13.1046 14 12V4C14 2.89543 13.1046 2 12 2Z" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M10 1V3M6 1V3M2 6H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              Purchased on {formatDate(order.updated_at)}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.orderCardBody}>
        <div className={styles.orderBodyGrid}>
          <div className={styles.orderSection}>
            <div className={styles.sectionLabel}>Activation Code</div>
            {order.redemption_ids && order.redemption_ids.length > 0 ? (
              <RedeemKey redemption_id={order.redemption_ids[0]} />
            ) : (
              <div className={styles.noKeyMessage}>No activation code available</div>
            )}
          </div>

          <div className={styles.orderSection}>
            <div className={styles.sectionLabel}>Order Status</div>
            <OrderStatus
              status={order.order_item_status}
              orderId={order.order_id}
              itemId={order.order_item_id}
              onTooltipChange={setIsTooltipActive}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const OrdersRedesign: React.FC = () => {
  const allOrders = useLoaderData<OrderItem[]>();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const confirmedOrderIdRef = useRef<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(5);
  const ITEMS_PER_PAGE = 5;
  const displayedOrders = allOrders.slice(0, visibleCount);
  const hasMore = visibleCount < allOrders.length;
  const totalOrders = allOrders.length;

  usePageTitle({
    title: 'My Orders | OnePlay Store',
    description: 'View and manage your game purchases, download keys, and track order status on OnePlay Store.'
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'dark');
  }, []);

  const loadMoreRef = useRef<HTMLDivElement>(null);

  const handleLoadMore = () => {
    const previousCount = visibleCount;
    setVisibleCount(prev => Math.min(prev + ITEMS_PER_PAGE, totalOrders));
    
    setTimeout(() => {
      if (loadMoreRef.current) {
        const cards = document.querySelectorAll(`.${styles.orderCard}`);
        if (cards[previousCount]) {
          cards[previousCount].scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }
      }
    }, 100);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const orderId = params.get('order_id');
    
    if (orderId) {
      showToast('Order Successful');

      const matchingOrders = allOrders.filter(order => order.order_id === orderId);

      // OpenPanel (WEB-4044): the gateway return is the only client-side
      // success signal — conclude checkout_completed + record revenue here.
      // Ref guard: StrictMode double-invoke / re-renders before the URL
      // param is stripped must not double-count the purchase.
      if (confirmedOrderIdRef.current === orderId) {
        return;
      }
      confirmedOrderIdRef.current = orderId;
      const confirmedTotal = matchingOrders.reduce(
        (sum, order) =>
          sum +
          (parseFloat(order.total_brought_price_display.replace(/[^\d.-]/g, '')) ||
            order.total_brought_price ||
            0),
        0
      );
      pageTrackingService.trackPurchaseConfirmed(
        orderId,
        matchingOrders.length > 0 ? confirmedTotal : undefined,
        matchingOrders[0]?.currency
      );

      if (matchingOrders.length > 0) {
        matchingOrders.forEach(order => {
          const priceValue = parseFloat(order.total_brought_price_display.replace(/[^\d.-]/g, '')) || order.total_brought_price;

          gtagService.trackPurchase({
            transaction_id: order.order_id,
            value: priceValue,
            currency: order.currency || 'USD',
            items: [{
              item_id: order.order_item_id,
              item_name: order.title,
              price: priceValue,
              quantity: order.quantity
            }]
          });
        });
      }
      
      params.delete('order_id');
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [location.search, navigate, showToast, allOrders]);

  useEffect(() => {
    pageTrackingService.trackOrderHistoryView(allOrders.length);
  }, [allOrders.length]);

  return (
    <>
      <Header />

      <div className={styles.ordersPage}>
        <div className={styles.ordersHeader}>
          <div className={styles.ordersHeaderContent}>
            <h1 className={styles.pageTitle}>My Orders</h1>
            {allOrders.length > 0 && (
              <p className={styles.pageSubtitle}>
                {allOrders.length} {allOrders.length === 1 ? 'order' : 'orders'} found
                {hasMore && ` • Showing ${displayedOrders.length} of ${allOrders.length}`}
              </p>
            )}
          </div>
        </div>

        <div className={styles.ordersContent}>
          {allOrders.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyStateIcon}>
                <svg width="120" height="120" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5M12 12H15M12 16H15M9 12H9.01M9 16H9.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h2 className={styles.emptyStateTitle}>Your Order history is empty!</h2>
              <p className={styles.emptyStateText}>Go grab some great deals.</p>
              <button
                type="button"
                className={styles.emptyStateButton}
                onClick={() => navigate("/")}
              >
                Browse Products
              </button>
            </div>
          ) : (
            <>
              <div className={styles.ordersList}>
                {displayedOrders.map((order) => (
                  <OrderCard key={order.order_item_id} order={order} />
                ))}
              </div>
              {hasMore && (
                <div className={styles.loadMoreContainer} ref={loadMoreRef}>
                  <button
                    className={styles.loadMoreButton}
                    onClick={handleLoadMore}
                    aria-label={`Load ${Math.min(ITEMS_PER_PAGE, allOrders.length - displayedOrders.length)} more orders`}
                  >
                    <span>Load More Orders</span>
                    <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className={styles.loadMoreCount}>
                      ({allOrders.length - displayedOrders.length} remaining)
                    </span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Footer />
    </>
  );
};

export default OrdersRedesign;

